import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { Paginator } from "primereact/paginator";
import * as Yup from "yup";
import {
  getIncentiveList,
  postIncentiveAddUpdate,
  postIncentiveDelete,
} from "./Services/PayrollService";
import { GetMasterTypeData } from "../../../Services/Global-Services/GlobalServices";
import { getEmployeeList } from "../EmployeeList/Service/employeeService";
import { TostMsg } from "../../../App";

// ---------- helpers ----------
const rup = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : "₹" + Number(n).toLocaleString("en-IN");

// API wants the numeric employee id; prefer `EmpId`, fall back to `Id`.
const empKey = (e) => e?.EmpId ?? e?.Id;
const findEmployee = (list, ref) =>
  list.find(
    (e) =>
      String(empKey(e)) === String(ref) ||
      String(e.Id) === String(ref) ||
      String(e.EmploymentId) === String(ref),
  );

const TAX_OPTIONS = ["Taxable", "Non-Taxable"];

const EMPTY_FORM = {
  EmpId: "",
  IncentiveTypeId: "",
  Amount: "",
  ForPeriod: "",
  Description: "",
  IncentiveStatusId: "",
  TaxTreatment: "Taxable",
  Notes: "",
};

const FieldError = ({ message }) =>
  message ? (
    <small style={{ color: "var(--danger)", display: "block", marginTop: "4px" }}>
      {message}
    </small>
  ) : null;

const IconTrophy = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M3 2a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v1h1a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-.5L12 13H4L2.5 8H2a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h1V2z" />
  </svg>
);
const IconCheck = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
    <path d="M13.854 3.146a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.793l6.646-6.647a.5.5 0 0 1 .708 0z" />
  </svg>
);
const IconTrash = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
    <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1z" />
  </svg>
);

const IncentiveManagement = () => {
  const toaster = useContext(TostMsg);
  const userDetails = useMemo(
    () => JSON.parse(localStorage.getItem("User") || "{}"),
    [],
  );

  // lookups
  const [incentiveStatusList, setIncentiveStatusList] = useState([]);
  const [incentiveTypesList, setIncentiveTypesList] = useState([]);
  const [formonthList, setFormonthList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);

  // list
  const [summary, setSummary] = useState(null);
  const [incentives, setIncentives] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  // filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const searchTimeout = useRef(null);

  // modals / form
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editIncentive, setEditIncentive] = useState(null);
  const [deleteIncentive, setDeleteIncentive] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // ---------- validation ----------
  const schema = useMemo(
    () =>
      Yup.object().shape({
        EmpId: Yup.string().required("Employee is required"),
        IncentiveTypeId: Yup.string().required("Incentive type is required"),
        Amount: Yup.number()
          .typeError("Amount must be a number")
          .required("Amount is required")
          .positive("Amount must be greater than 0")
          .max(10000000, "Amount cannot exceed ₹1,00,00,000")
          .test(
            "two-decimals",
            "Use at most 2 decimal places",
            (v) => v === undefined || Math.round(v * 100) / 100 === v,
          )
          .test(
            "not-below-paid",
            "Amount cannot be less than what is already paid",
            function (v) {
              const paid = this.options.context?.paid || 0;
              return v === undefined || v >= paid;
            },
          ),
        ForPeriod: Yup.string(),
        Description: Yup.string().max(250, "Description cannot exceed 250 characters"),
        IncentiveStatusId: Yup.string().required("Status is required"),
        TaxTreatment: Yup.string()
          .required("Tax treatment is required")
          .oneOf(TAX_OPTIONS, "Select a valid tax treatment"),
        Notes: Yup.string().max(500, "Notes cannot exceed 500 characters"),
      }),
    [],
  );

  const ctx = { paid: editIncentive?.PaidAmount || 0 };

  const validateField = async (name, data = formData) => {
    try {
      await schema.validateAt(name, data, { context: ctx });
      setFormErrors((p) => ({ ...p, [name]: "" }));
    } catch (err) {
      setFormErrors((p) => ({ ...p, [name]: err.message }));
    }
  };

  const validateForm = async () => {
    try {
      await schema.validate(formData, { abortEarly: false, context: ctx });
      setFormErrors({});
      return true;
    } catch (err) {
      const errors = {};
      err.inner.forEach((e) => {
        if (!errors[e.path]) errors[e.path] = e.message;
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...formData, [name]: value };
    setFormData(next);
    if (formErrors[name]) validateField(name, next);
  };

  const handleBlur = (e) => validateField(e.target.name);

  const applyServerErrors = (res) => {
    const serverErrors = res?.Errors || res?.errors;
    if (!serverErrors || typeof serverErrors !== "object") return false;
    const entries = Array.isArray(serverErrors)
      ? serverErrors.map((e) => [e.Field || e.field || e.PropertyName, e.Message || e.message])
      : Object.entries(serverErrors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]);
    const mapped = {};
    entries.forEach(([key, msg]) => {
      const field = Object.keys(EMPTY_FORM).find(
        (f) => f.toLowerCase() === String(key || "").toLowerCase(),
      );
      if (field && msg) mapped[field] = msg;
    });
    if (!Object.keys(mapped).length) return false;
    setFormErrors((p) => ({ ...p, ...mapped }));
    return true;
  };

  // ---------- loading ----------
  useEffect(() => {
    loadMasterData();
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMasterData = async () => {
    try {
      const base = { allRecords: true, organisationId: userDetails?.OrgId };
      const [statusRes, typeRes, monthRes] = await Promise.all([
        GetMasterTypeData({ ...base, masterType: "IncentiveStatus" }),
        GetMasterTypeData({ ...base, masterType: "IncentiveTypes" }),
        GetMasterTypeData({ ...base, masterType: "formonth" }),
      ]);
      setIncentiveStatusList(statusRes?.Data || []);
      setIncentiveTypesList(typeRes?.Data || []);
      setFormonthList(monthRes?.Data || []);
    } catch (error) {
      console.error("Error loading master data:", error);
      toaster?.current?.error("Could not load incentive master data.");
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await getEmployeeList({
        OrganisationId: userDetails?.OrgId,
        LoggedInEmployeeId: userDetails?.EpmId,
        IsDirectReportOrHierarchy: 0,
        EmployeeId: 0,
        SearchText: "",
        FollowersOnly: 0,
        FollowingsOnly: 0,
        IsSuggestedOnly: 0,
        IsExEmployeesAlso: 0,
        pageNumber: 1,
        pageSize: 10000,
      });
      setEmployeeList(res?.Data || []);
    } catch (error) {
      console.error("Error fetching employee list:", error);
      toaster?.current?.error("Could not load employee list.");
    }
  };

  const fetchIncentives = async (page = 1, size = rows) => {
    setIsLoading(true);
    try {
      const res = await getIncentiveList({
        IncentiveId: 0,
        OrgId: userDetails?.OrgId,
        GUID: "",
        EmpId: 0,
        PageNumber: page,
        PageSize: size,
        Search: searchQuery || "",
        IncentiveStatusId: filterStatus || "",
        IncentiveTypeId: filterType || "",
        LoggedInEmpId: 0,
      });
      const items = res?.Data?.IncentiveResponseDetails || [];
      setSummary(res?.Data?.IncentiveDashboardDetails?.[0] || null);
      setIncentives(items);
      setTotalRecords(
        res?.Data?.RecordCount ??
          res?.Data?.TotalRecords ??
          res?.Data?.TotalCount ??
          items.length,
      );
    } catch (error) {
      console.error("Error fetching incentive list:", error);
      toaster?.current?.error("Could not load incentives. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setFirst(0);
      fetchIncentives(1, rows);
    }, 500);
    return () => clearTimeout(searchTimeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterType, filterStatus]);

  const currentPage = () => Math.floor(first / rows) + 1;

  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
    fetchIncentives(event.first / event.rows + 1, event.rows);
  };

  // ---------- open / close ----------
  const defaultStatusId = () => {
    const open = incentiveStatusList.find(
      (s) => String(s.Name).toLowerCase() === "open",
    );
    return String((open || incentiveStatusList[0])?.Id ?? "");
  };

  const openAdd = () => {
    setEditIncentive(null);
    setFormErrors({});
    setFormData({ ...EMPTY_FORM, IncentiveStatusId: defaultStatusId() });
    setShowModal(true);
  };

  const openEdit = (v) => {
    const type =
      incentiveTypesList.find((t) => String(t.Id) === String(v.IncentiveTypeId)) ||
      incentiveTypesList.find((t) => t.Name === v.IncentiveTypeName);
    const status =
      incentiveStatusList.find((s) => String(s.Id) === String(v.IncentiveStatusId)) ||
      incentiveStatusList.find((s) => s.Name === v.IncentiveStatusName);
    const emp = findEmployee(employeeList, v.EmpId);

    setEditIncentive(v);
    setFormErrors({});
    setFormData({
      EmpId: emp ? String(empKey(emp)) : "",
      IncentiveTypeId: type ? String(type.Id) : "",
      Amount: v.Amount ?? "",
      ForPeriod: v.ForPeriod || "",
      Description: v.Description || "",
      IncentiveStatusId: status ? String(status.Id) : "",
      TaxTreatment: v.TaxTreatment || "Taxable",
      Notes: v.Notes || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (!isSaving) setShowModal(false);
  };

  // ---------- CRUD ----------
  const saveIncentive = async () => {
    if (isSaving) return;
    if (!(await validateForm())) return;

    const f = formData;
    const params = {
      Id: editIncentive?.Id || 0,
      OrgId: userDetails?.OrgId || 0,
      EmpId: Number(f.EmpId),
      IncentiveTypeId: Number(f.IncentiveTypeId),
      Amount: Number(f.Amount),
      PaidAmount: editIncentive?.PaidAmount ?? 0, // keep what's already paid
      ForPeriod: f.ForPeriod || "",
      Description: f.Description.trim(),
      TaxTreatment: f.TaxTreatment,
      IncentiveStatusId: Number(f.IncentiveStatusId),
      Notes: f.Notes.trim(),
    };

    setIsSaving(true);
    try {
      const res = await postIncentiveAddUpdate(params);
      if (res?.StatusCode === 2000) {
        toaster?.current?.success(
          `Incentive ${editIncentive ? "updated" : "added"} successfully`,
        );
        setShowModal(false);
        if (editIncentive) {
          fetchIncentives(currentPage(), rows);
        } else {
          setFirst(0);
          fetchIncentives(1, rows);
        }
      } else {
        const shown = applyServerErrors(res);
        toaster?.current?.error(
          res?.Message ||
            (shown ? "Please fix the highlighted fields." : "Error saving incentive. Please try again."),
        );
      }
    } catch (error) {
      console.error("Error saving incentive:", error);
      toaster?.current?.error("Error saving incentive. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const askDelete = (v) => {
    setDeleteIncentive(v);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteIncentive || isDeleting) return;
    setIsDeleting(true);
    try {
      // The API returns { StatusCode: 2000 } on success (not `.success`)
      const res = await postIncentiveDelete({ RequestId: deleteIncentive.Id });
      if (res?.StatusCode === 2000) {
        toaster?.current?.success("Incentive deleted successfully");
        setShowDeleteModal(false);
        setDeleteIncentive(null);
        let page = currentPage();
        if (incentives.length === 1 && page > 1) {
          page -= 1;
          setFirst((page - 1) * rows);
        }
        fetchIncentives(page, rows);
      } else {
        toaster?.current?.error(res?.Message || "Error deleting incentive");
      }
    } catch (error) {
      console.error("Error deleting incentive:", error);
      toaster?.current?.error("Error deleting incentive. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // ---------- export (current page) ----------
  const exportToCSV = () => {
    if (!incentives.length) {
      toaster?.current?.error("No data to export.");
      return;
    }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = [
      "Employee", "Employee ID", "Type", "Description", "For Period",
      "Amount", "Paid Amount", "Status", "Tax", "Notes",
    ];
    const body = incentives.map((v) => [
      v.EmployeeName, v.EmpId, v.IncentiveTypeName, v.Description, v.ForPeriod,
      v.Amount, v.PaidAmount, v.IncentiveStatusName, v.TaxTreatment, v.Notes,
    ]);
    const csv = [headers, ...body].map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "incentives_data.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const pageTotalAmount = useMemo(
    () => incentives.reduce((a, v) => a + (v.Amount || 0), 0),
    [incentives],
  );

  const statusClassFor = (name) => {
    const n = String(name).toLowerCase();
    if (n === "open") return "badge-pending";
    if (n === "paid") return "badge-paid";
    return "badge-locked";
  };

  // ---------- render ----------
  return (
    <div className="container-fluid">
      <div className="form-head d-flex flex-wrap align-items-center page-head mb-0">
        <div className="me-auto">
          <h2 className="font-w600 mb-0">Incentive &amp; Bonus Management</h2>
          <p>
            Variable pay, performance incentives, retention and statutory
            bonuses. Open/partial records flow into payroll Step 5.
          </p>
        </div>
        <div className="rightside-tabs">
          <div className="topbar-actions flex items-center gap-2">
            <button className="btn-og btn-og-secondary btn-og-sm" onClick={exportToCSV}>
              <i className="fa-solid fa-file-excel"></i> Export
            </button>
            <button onClick={openAdd} className="btn-og theme-color btn-og-sm">
              Add Incentive
            </button>
          </div>
        </div>
      </div>

      <div className="page-content flex-1 overflow-auto p-6">
        {/* SUMMARY */}
        <div className="incentives-summary">
          {[
            ["Open Records", summary?.OpenRecords ?? 0, false],
            ["Total Incentive Pool", summary?.TotalIncentivePool ?? 0, true],
            ["Paid Out", summary?.PaidOut ?? 0, true],
            ["Pending Payout", summary?.PendingPayout ?? 0, true],
          ].map(([label, value, money]) => (
            <div className="blocks" key={label}>
              <div className="block-div1">{label}</div>
              <div className="block-div2">{money ? rup(value) : value}</div>
            </div>
          ))}
        </div>

        {/* TABLE */}
        <div className="card bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
          <div className="toolbar filter-row pt-2">
            <input
              type="text"
              className="form-control w-52"
              placeholder="Search employee…"
              style={{ width: "200px" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="toolbar-sep" />
            <select
              className="fs-select w-44"
              value={filterType}
              style={{ width: "160px" }}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              {incentiveTypesList.map((t) => (
                <option key={t.Id} value={t.Id}>{t.Name}</option>
              ))}
            </select>
            <select
              className="fs-select w-40"
              value={filterStatus}
              style={{ width: "160px" }}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              {incentiveStatusList.map((s) => (
                <option key={s.Id} value={s.Id}>{s.Name}</option>
              ))}
            </select>
            <div className="flex-1" />
            <span className="text-sm text-gray-500">{totalRecords} records</span>
          </div>

          <div className="overflow-x-auto">
            <table className="mgmt-tbl w-full">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th className="tr">Amount</th>
                  <th>For Period</th>
                  <th>Tax</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "24px" }}>
                      Loading incentives…
                    </td>
                  </tr>
                )}
                {!isLoading && incentives.length === 0 && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "24px" }}>
                      No records found. Change the filters or add an incentive.
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  incentives.map((v) => (
                    <tr key={v.Id}>
                      <td>
                        <span style={{ fontWeight: 600 }}>{v.EmployeeName}</span>
                        <br />
                        <code style={{ fontSize: "10px", color: "var(--brand)" }}>
                          {v.EmployeeCode || v.EmpId}
                        </code>
                      </td>
                      <td>
                        <span className="badge badge-active" style={{ fontSize: "10.5px" }}>
                          {v.IncentiveTypeName}
                        </span>
                      </td>
                      <td style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                        {v.Description || "—"}
                      </td>
                      <td className="tr" style={{ fontWeight: 700 }}>{rup(v.Amount)}</td>
                      <td style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                        {v.ForPeriod || "—"}
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: "11px",
                            color: v.TaxTreatment === "Taxable" ? "var(--danger)" : "var(--success)",
                          }}
                        >
                          {v.TaxTreatment}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span className={`badge ${statusClassFor(v.IncentiveStatusName)}`}>
                          {v.IncentiveStatusName}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                          <button className="btn btn-light btn-sm" onClick={() => openEdit(v)} title="Edit">
                            <i className="fa-solid fa-pen"></i>
                          </button>
                          <button
                            className="btn btn-light btn-sm"
                            onClick={() => askDelete(v)}
                            title="Delete"
                            style={{ color: "var(--danger)" }}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3">PAGE TOTAL</td>
                  <td className="tr" style={{ fontWeight: 700 }}>{rup(pageTotalAmount)}</td>
                  <td colSpan="4">—</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <Paginator
            first={first}
            rows={rows}
            totalRecords={totalRecords}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={onPageChange}
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          />
        </div>
      </div>

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="mo open" onClick={closeModal}>
          <div className="mb" onClick={(e) => e.stopPropagation()}>
            <div className="mh">
              <div
                style={{ width: "30px", height: "30px", borderRadius: "8px", background: "var(--brand-soft)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--brand)" }}
              >
                <IconTrophy />
              </div>
              <h6 className="font-bold text-base">
                {editIncentive ? "Edit Incentive" : "Add Incentive"}
              </h6>
              <button
                onClick={closeModal}
                aria-label="Close"
                style={{ marginLeft: "auto", background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#888" }}
              >
                ×
              </button>
            </div>

            <div className="mbody">
              <div className="fsec">Employee &amp; Incentive Details</div>
              <div className="fg2">
                <div>
                  <label className="form-label">
                    Employee <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="EmpId"
                    value={formData.EmpId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.EmpId ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {employeeList.map((emp) => (
                      <option key={emp.Id} value={empKey(emp)}>
                        {emp.FullName} ({emp.EmploymentId})
                      </option>
                    ))}
                  </select>
                  <FieldError message={formErrors.EmpId} />
                </div>

                <div>
                  <label className="form-label">
                    Incentive Type <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="IncentiveTypeId"
                    value={formData.IncentiveTypeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.IncentiveTypeId ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {incentiveTypesList.map((t) => (
                      <option key={t.Id} value={t.Id}>{t.Name}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.IncentiveTypeId} />
                </div>

                <div>
                  <label className="form-label">
                    Amount (₹) <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control w-100"
                    name="Amount"
                    min="1"
                    step="0.01"
                    placeholder="0"
                    value={formData.Amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.Amount ? { borderColor: "var(--danger)" } : {}}
                  />
                  <FieldError message={formErrors.Amount} />
                </div>

                <div>
                  <label className="form-label">For Period</label>
                  <select
                    className="form-select w-100"
                    name="ForPeriod"
                    value={formData.ForPeriod}
                    onChange={handleChange}
                  >
                    <option value="">Select…</option>
                    {formonthList.map((m) => (
                      <option key={m.Id} value={m.Name}>{m.Name}</option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-control w-100"
                    name="Description"
                    maxLength="250"
                    placeholder="Brief description…"
                    value={formData.Description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldError message={formErrors.Description} />
                </div>
              </div>

              <div className="fsec">Status &amp; Tax</div>
              <div className="fg2">
                <div>
                  <label className="form-label">
                    Status <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="IncentiveStatusId"
                    value={formData.IncentiveStatusId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.IncentiveStatusId ? { borderColor: "var(--danger)" } : {}}
                  >
                    <option value="">Select…</option>
                    {incentiveStatusList.map((s) => (
                      <option key={s.Id} value={s.Id}>{s.Name}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.IncentiveStatusId} />
                </div>

                <div>
                  <label className="form-label">
                    Tax Treatment <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="TaxTreatment"
                    value={formData.TaxTreatment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.TaxTreatment ? { borderColor: "var(--danger)" } : {}}
                  >
                    {TAX_OPTIONS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.TaxTreatment} />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control w-100"
                    name="Notes"
                    rows="2"
                    maxLength="500"
                    placeholder="Approval reference, criteria…"
                    value={formData.Notes}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    style={formErrors.Notes ? { borderColor: "var(--danger)" } : {}}
                  />
                  <FieldError message={formErrors.Notes} />
                </div>
              </div>
            </div>

            <div className="mfoot">
              <button className="btn btn-light" onClick={closeModal} disabled={isSaving}>
                Cancel
              </button>
              <button className="btn btn-brand" onClick={saveIncentive} disabled={isSaving}>
                <IconCheck />{" "}
                <span>{isSaving ? "Saving…" : editIncentive ? "Save Changes" : "Add Incentive"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {showDeleteModal && deleteIncentive && (
        <div className="mo open" onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div
            style={{ background: "#fff", borderRadius: "12px", width: "400px", padding: "24px", boxShadow: "0 24px 64px rgba(0,0,0,.18)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <div style={{ width: "34px", height: "34px", background: "#fee2e2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--danger)" }}>
                <IconTrash />
              </div>
              <h6 style={{ margin: 0, fontWeight: 700 }}>Delete Incentive</h6>
            </div>
            <p style={{ fontSize: "13px", color: "var(--text-mid)", margin: "0 0 18px" }}>
              Delete the {deleteIncentive.IncentiveTypeName} incentive of {rup(deleteIncentive.Amount)} for{" "}
              {deleteIncentive.EmployeeName}?
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button className="btn btn-light" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                Cancel
              </button>
              <button
                className="btn btn-brand"
                style={{ background: "var(--danger)", borderColor: "var(--danger)" }}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncentiveManagement;
